// Shared price range slider — same view in AddNewProject and AddNewConfiguration
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Slider } from '@mui/material';

// ── Constants (export for parents that need defaults) ────────────────────────
export const PRICE_MIN = 0;
export const PRICE_MAX = 10_00_00_000;   // 10 Cr
export const DEFAULT_PRICE_MAX = 1_00_00_000; // 1 Cr default
export const PRICE_STEP = 1_00_000;

const C = {
  green: '#2e7d32',
  greenLight: 'rgba(46,125,50,0.10)',
};

export function formatPrice(val) {
  if (val === 0) return '₹0';
  if (val >= 1_00_00_000) return `₹${(val / 1_00_00_000).toFixed(val % 1_00_00_000 === 0 ? 0 : 1)} Cr`;
  if (val >= 1_00_000) return `₹${(val / 1_00_000).toFixed(val % 1_00_000 === 0 ? 0 : 1)} L`;
  return `₹${val.toLocaleString('en-IN')}`;
}

/**
 * Price range slider — value is [min, max] in rupees.
 * Same UI as Add New Project: label, slider, min/max labels below.
 * @param {[number, number]} value - [minPrice, maxPrice]
 * @param {(v: [number, number]) => void} onChange - called on commit
 * @param {string} [error] - optional error message below slider
 * @param {number} [min] - optional lower bound (default PRICE_MIN)
 * @param {number} [max] - optional upper bound (default PRICE_MAX)
 */
export default function PriceRangeSlider({ value, onChange, error, min: minProp, max: maxProp }) {
  const min = minProp != null && !Number.isNaN(Number(minProp)) ? Number(minProp) : PRICE_MIN;
  const max = maxProp != null && !Number.isNaN(Number(maxProp)) ? Number(maxProp) : PRICE_MAX;
  const safeMin = Math.min(min, max);
  const safeMax = Math.max(min, max);

  const clamp = (v) => [
    Math.max(safeMin, Math.min(safeMax, v[0] ?? safeMin)),
    Math.max(safeMin, Math.min(safeMax, v[1] ?? safeMax)),
  ];

  const defaultVal = [safeMin, Math.max(safeMin, Math.min(safeMax, DEFAULT_PRICE_MAX))];
  const [local, setLocal] = useState(() => (value && Array.isArray(value) ? clamp(value) : defaultVal));

  useEffect(() => {
    if (value && Array.isArray(value)) setLocal(clamp(value));
  }, [value, safeMin, safeMax]);

  // 5 evenly-spaced marks across the range (project min–max for config, full range for project)
  const marks = useMemo(() => {
    return [0, 25, 50, 75, 100].map((pct) => {
      const raw = safeMin + (pct / 100) * (safeMax - safeMin);
      const val = Math.round(raw / PRICE_STEP) * PRICE_STEP;
      return { value: Math.max(safeMin, Math.min(safeMax, val)), label: formatPrice(val) };
    });
  }, [safeMin, safeMax]);

  return (
    <Box>
      <Box sx={{ mb: 0.75 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a' }}>
          Price range: {formatPrice(local[0])} – {formatPrice(local[1])}
        </Typography>
      </Box>
      <Box sx={{ px: 0.5 }}>
        <Slider
          value={local}
          onChange={(_, v) => setLocal(clamp(v))}
          onChangeCommitted={(_, v) => onChange(clamp(v))}
          min={safeMin}
          max={safeMax}
          step={PRICE_STEP}
          marks={marks}
          valueLabelDisplay="auto"
          valueLabelFormat={formatPrice}
          sx={{
            color: C.green,
            height: 5,
            '& .MuiSlider-thumb': {
              width: 18,
              height: 18,
              bgcolor: 'white',
              border: `2.5px solid ${C.green}`,
              boxShadow: `0 1px 6px ${C.greenLight}`,
              '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 8px ${C.greenLight}` },
            },
            '& .MuiSlider-track': {
              background: `linear-gradient(90deg, ${C.greenLight}, ${C.green})`,
              border: 'none',
            },
            '& .MuiSlider-rail': { bgcolor: '#e0e0e0', opacity: 1 },
            '& .MuiSlider-valueLabel': {
              bgcolor: C.green,
              fontSize: 10,
              fontWeight: 700,
              borderRadius: 1,
            },
            '& .MuiSlider-mark': {
              width: 4,
              height: 4,
              borderRadius: '50%',
              bgcolor: '#bdbdbd',
              transform: 'translate(-50%, -50%)',
              '&.MuiSlider-markActive': {
                bgcolor: C.green,
                opacity: 0.7,
              },
            },
            '& .MuiSlider-markLabel': {
              fontSize: 9,
              color: '#bbb',
              mt: 0.25,
            },
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.75 }}>
        <Typography sx={{ fontSize: 9, color: '#bbb' }}>{formatPrice(safeMin)}</Typography>
        <Typography sx={{ fontSize: 9, color: '#bbb' }}>{formatPrice(safeMax)}</Typography>
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
